import { useEffect, useMemo, useState } from "react";
import { CategoryWithParts, ICategorySelectedInMultipleTables, IPart } from "../../../../models/Part";
import { PartCategoryConflictMap } from "../../../../models/PartCategorySubPart";
import PriceFormat from "../../../../utils/priceFormat";
import { formatMinutesIntoHours } from "../../../../utils/formatMinutesIntoHours";
import { ButtonInput } from "../../../shared/Inputs/ButtonInput";
import { CreateJobRequest, JobType } from "../../../../models/Job";
import { ILead } from "../../../../models/Lead";
import { IVehicle } from "../../../../models/Vehicle";
import useDefaultCRUDHandlers from "../../../../hooks/useDefaultCRUDHandlers";
import useJobMutations from "../../../../data/useJobMutations";
import usePartMutations from "../../../../data/usePartMutations";
import { DateTime } from "luxon";
import useRsAutomotive from "../../../../data/useRsAutomotive";
import useRsCoreAccount from "../../../../data/useRsCoreAccount";
import useSettingSet from "../../../../data/useSettingSet";


const SummaryCard = ({ title, time, price, total }: { 
  title: string, 
  time?: number | null, 
  price: number,
  total?: number | null
}) => (
  <div className="bg-white shadow rounded-lg p-4 w-full">
    <h1 className="text-lg font-semibold">{title}</h1>
    <div className="flex justify-between">
      {typeof time === "number" && (
        <div>
          <p className="text-gray-600 text-sm">Est. Time</p>
          <p>{formatMinutesIntoHours(time)}</p>
        </div>
      )}
      {typeof total === "number" && (
        <div>
          <p className="text-gray-600 text-sm">Total</p>
          <p>{total}</p>
        </div>
      )}
      <div>
        <p className="text-gray-600 text-sm">Price</p>
        <p>{PriceFormat(price)}</p>
      </div>
    </div>
  </div>
);

interface SelectedCategoriesProps {
  selectedCategories: CategoryWithParts[] | undefined;
  setSelectedCategories: any;
  setSelectedByTableId: any;
  lead: ILead | null;
  vehicle: IVehicle | null;
  /** Global (cross-table) main/sub-part clashes, keyed by category id. */
  categoryConflicts?: PartCategoryConflictMap;
  /**
   * Categories ticked in more than one table - a category listed by two assemblies appears in
   * both of their tables. Each selection creates its own part, so this warns rather than
   * blocks: ticking one twice is how the operator asks for two of it.
   */
  categoriesSelectedInMultipleTables?: ICategorySelectedInMultipleTables[];
}

export default function SelectedCategories({ selectedCategories, lead, vehicle, setSelectedCategories, setSelectedByTableId, categoryConflicts, categoriesSelectedInMultipleTables }: SelectedCategoriesProps) {
  const { create } = useJobMutations();
  const { createParts } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Job");
  const { saveHandlers: partsSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { isSet: hasRsKey } = useSettingSet("Rs:ApiKey");
  const { order } = useRsAutomotive();
  const { rsCoreAccount } = useRsCoreAccount();
  const [totalTime, setTotalTime] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [bodyTime, setBodyTime] = useState<number>(0);
  const [mechTime, setMechTime] = useState<number>(0);
  const [bodyPrice, setBodyPrice] = useState<number>(0);
  const [mechPrice, setMechPrice] = useState<number>(0);
  
  const [rsTotal, setRsTotal] = useState<number>(0);
  const [rsPrice, setRsPrice] = useState<number>(0);


  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return;
    }

    const asNumberOrZero = (value: number | null | undefined) => {
      if (typeof value !== "number" || Number.isNaN(value)) return 0;
      return value;
    };

    const getCategoryTime = (cat: CategoryWithParts) => {
      return asNumberOrZero(cat.category.whTime) + asNumberOrZero(cat.category.disTime);
    };

    // Calculate total time and price
    setTotalTime(selectedCategories.reduce((sum, cat) => sum + getCategoryTime(cat), 0));
    setTotalPrice(selectedCategories.reduce((sum, cat) => sum + asNumberOrZero(cat.price), 0));

    // Calculate body work time and price
    setBodyTime(selectedCategories.reduce((sum, cat) => sum + (!cat.isMech ? getCategoryTime(cat) : 0), 0));
    setBodyPrice(selectedCategories.reduce((sum, cat) => sum + (!cat.isMech ? asNumberOrZero(cat.price) : 0), 0));

    // Calculate mechanical work time and price
    setMechTime(selectedCategories.reduce((sum, cat) => sum + (cat.isMech ? getCategoryTime(cat) : 0), 0));
    setMechPrice(selectedCategories.reduce((sum, cat) => sum + (cat.isMech ? asNumberOrZero(cat.price) : 0), 0));

    setRsTotal(selectedCategories.filter(cat => cat.isCore).length);
    setRsPrice(selectedCategories.reduce((sum, cat) => sum + (cat.isCore ? asNumberOrZero(cat.category.rsPrice) : 0), 0));

  }, [selectedCategories]);

  const handleCreateJob = async () => {

    if (!selectedCategories || selectedCategories.length === 0) return;

    let rsRes = await handleRsOrder();
    if (rsRes != null && rsRes.rs_status != "success") {
      saveHandlers.onError();
      return;
    }

    const newJob: CreateJobRequest = {
      type: JobType.Generic, //All jobs are generic now
      title: `${vehicle?.combinedMake} ${vehicle?.modelRangeDescription} - ${vehicle?.vrm}`,
      description: selectedCategories.map(cat => cat.category.name).join(", "),
      duration: totalTime,
      estimatedPrice: totalPrice, //If everything sells at the price entered
      estimatedMargin: null, //I don't think this is useful anymore as we don't have it for new parts
      leadId: lead?.id ?? null,
      vehicleId: vehicle?.id,
      jobPartCategories: selectedCategories.map(cat => ({
        partCategory: cat.category,
        averagePrice: cat.category.averagePrice,
        price: (cat.isCore ? cat.category.rsPrice : cat.price) ?? 0,
        lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
        note: cat.note,
        coreOrderNumber: cat.isCore ? rsRes.rs_data.rs_sales_order_number : null,
        accountId: cat.isCore ? (rsCoreAccount.data?.data?.id ?? null) : (cat.accountId ?? null),
        physicalRouting: cat.physicalRouting ?? null
      })),
    }

    // console.log("new Job", newJob)

    create.mutate(newJob, {
      ...saveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      }
    });
  }

  const handleRsOrder = async () => {
    if (!selectedCategories || selectedCategories.length === 0) return null;

    var rsCategories = selectedCategories.filter(sc => sc.isCore);
    if (!rsCategories || rsCategories.length === 0) return null;

    console.log("-------- RS Categories --------");
    console.log(rsCategories);

    var res = await order.mutateAsync({
      rs_ktype:  rsCategories[0].category.rsData.rs_ktype,
      rs_regno:  rsCategories[0].category.rsData.rs_regno,
      rs_search: rsCategories[0].category.rsData.rs_search,
      rs_vendor: rsCategories[0].category.rsData.rs_vendor,
      rs_products: Object.fromEntries(
          rsCategories.map((rc) => {
            const categoryName = (rc.category.rsName ?? rc.category.name)?.trim();
            const rsItem = rc.category.rsItem;

            // skip invalid rows
            if (!categoryName || !rsItem) return null;

            return [categoryName, { rs_item: rsItem }] as const;
          }).filter(Boolean) as Array<readonly [string, { rs_item: string }]>
        ),
    });

    return res;
  }

  const handleCreateParts = async (isDamaged: boolean) => {
    if (!selectedCategories || selectedCategories.length === 0) return;

    let rsRes = await handleRsOrder();
    if (rsRes != null && rsRes.rs_status != "success") {
      saveHandlers.onError();
      return;
    }

    const newParts = selectedCategories.map(cat => ({
      categoryId: cat.category.id,
      price: (cat.isCore ? cat.category.rsPrice : cat.price) ?? 0,
      averagePrice: cat.category.averagePrice,
      lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
      note: cat.note,
      coreOrderNumber: cat.isCore ? rsRes.rs_data.rs_sales_order_number : null,
      accountId: cat.isCore ? (rsCoreAccount.data?.data?.id ?? null) : (cat.accountId ?? null),
      physicalRouting: cat.physicalRouting ?? null,
      isDamaged
    }));

    const body = {
      vehicleId: vehicle?.id,
      leadId: lead?.id,
      parts: newParts
    }

    // console.log("new parts", newParts);

    createParts.mutate(body, {
      ...partsSaveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      }
    });
  }

  const conflictCount = categoryConflicts?.size ?? 0;

  /**
   * Names of the "main" categories involved in a clash - the useful half of the message,
   * since resolving a conflict means deciding between the main and its sub-parts.
   */
  const conflictMainNames = useMemo(() => {
    if (!categoryConflicts || !selectedCategories) return [] as string[];

    return selectedCategories
      .filter((cat) => categoryConflicts.get(cat.category.id)?.role === "main")
      .map((cat) => cat.category.name);
  }, [categoryConflicts, selectedCategories]);

  const duplicateSelections = categoriesSelectedInMultipleTables ?? [];

  if (!selectedCategories || selectedCategories.length === 0) {
    return null;
  };

  return (
    <>
      <div className="flex flex-col sticky pb-2 top-14 z-30 bg-gray-100">

        <div className="flex items-center gap-x-8 mt-4">

          <div className="bg-white shadow rounded-lg p-4 w-full">
            <h1 className="text-lg font-semibold">Categories Selected</h1>
            <p className="text-2xl pt-3 flex items-center gap-x-4">{selectedCategories.length}
              {selectedCategories.some(cat => !cat.price) && (
                <span className="text-gray-600 text-sm"> - {selectedCategories.filter(cat => !cat.price).length} categories have no price</span>
              )}
              {conflictCount > 0 && (
                <span className="text-red-700 text-sm font-semibold">
                  - {conflictCount} conflicting {conflictCount === 1 ? "category" : "categories"} selected
                </span>
              )}
            </p>
            {conflictCount > 0 && (
              <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-800">
                {conflictMainNames.join(", ")} {conflictMainNames.length === 1 ? "is" : "are"} selected
                together with parts contained within {conflictMainNames.length === 1 ? "it" : "them"}.
                Highlighted in red below - you can still save.
              </p>
            )}
            {duplicateSelections.length > 0 && (
              <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                {duplicateSelections.length === 1 ? "This category is" : "These categories are"} selected
                in more than one assembly, so a separate part will be created for each selection:{" "}
                {duplicateSelections
                  .map((dup) => `${dup.categoryName} (${dup.tableNames.join(", ")})`)
                  .join("; ")}
                . Untick one if you only want a single part.
              </p>
            )}
          </div>

          <SummaryCard title="Total" time={totalTime} price={totalPrice} />
          <SummaryCard title="Body Work" time={bodyTime} price={bodyPrice} />
          <SummaryCard title="Mechanical Work" time={mechTime} price={mechPrice} />
          {hasRsKey && (
            <SummaryCard title="RS Automotive" total={rsTotal} price={rsPrice} />
          )}
        </div>

      </div>
      <div className="flex justify-between">
        <div className="flex gap-x-6">
          <ButtonInput label={`${lead?.jobs && lead.jobs.length > 0 ? "Update" : "Create"} Job`} onClick={() => handleCreateJob()} classes={undefined} isLoading={create.isLoading} />
          <ButtonInput label={"Make Available"} onClick={() => handleCreateParts(false)} classes={undefined} isLoading={createParts.isLoading} />
        </div>

        <div>
          <ButtonInput label={"Mark as Damaged"} onClick={() => handleCreateParts(true)} classes={"!bg-red-500 hover:!bg-red-600"} isLoading={createParts.isLoading} />
        </div>
      </div>
    </>
  )
}